import { Trans } from '@lingui/react/macro';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import Modal from '@mui/material/Modal';
import { useEffect, useState } from 'react';
import { getErrorMessage } from '../utils/getErrorMessage';
import { useNotificationMessages } from '../utils/useNotificationMessages';
import { useStatelessGet } from '../utils/useStatelessGet';

const PaymentModal = ({ open, onClose, onContinuePayment }: { open: boolean; onClose: () => void; onContinuePayment: () => Promise<void> }) => {
  const theme = useTheme();
  const [isLoading, setLoading] = useState(false);

  const handleContinuePayment = async () => {
    try {
      setLoading(true);
      await onContinuePayment();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal keepMounted open={open} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'hsla(0, 0%, 100%, 1)',
          border: `1px solid ${theme.palette.secondary.light}`,
          boxShadow: 'inset 8px 0 0 0 #0000),inset -8px 0 0 0 #0000)',
          p: 3,
          borderRadius: 2,
        }}
      >
        <Stack>
          <Typography variant="body1" component="h2" fontWeight="bold">
            <Trans>Payment Confirmation</Trans>
          </Typography>
          <Typography color={theme.palette.secondary.main} variant="caption" sx={{ mt: 1 }}>
            <Trans>You will be redirected to our official payment gateway. Please do not close this window or refresh the page during the process.</Trans>
          </Typography>
        </Stack>
        <Stack alignItems={isLoading ? 'center' : 'flex-end'}>
          {isLoading ? (
            <>
              <CircularProgress size={32} />
              <Stack mt={3}>
                <Typography textAlign="center" variant="body1">
                  <Trans>Please wait while we redirect you to our secure payment gateway...</Trans>
                </Typography>
              </Stack>
            </>
          ) : (
            <Button variant="contained" sx={{ mt: theme.spacing(2) }} onClick={handleContinuePayment} size="medium">
              <Typography component="span" fontSize="small">
                <Trans>Continue to Payment Gateway</Trans>
              </Typography>
            </Button>
          )}
        </Stack>
        <Button onClick={onClose} sx={{ position: 'absolute', right: 0, top: 0, p: 1, minWidth: 0 }}>
          <CloseIcon sx={{ color: theme.palette.secondary.main, width: theme.spacing(2), height: theme.spacing(2) }} />
        </Button>
      </Box>
    </Modal>
  );
};

interface NextStepsPaymentProps {
  contactId?: string;
  dealId?: string;
  paymentProvider?: 'verifone' | 'nexpora';
}

export const NextStepsPayment = ({ contactId, dealId, paymentProvider }: NextStepsPaymentProps) => {
  const theme = useTheme();
  const [gatewayLink, setGatewayLink] = useState('');
  const [open, setOpen] = useState(false);
  const getPaymentLink = useStatelessGet<{
    checkoutLink: string;
  }>(`/payments/Contacts/${contactId}/Deals/${dealId}/checkout-link`);
  const { showErrorMessage } = useNotificationMessages();

  const onContinueToGateway = async () => {
    if (contactId && dealId) {
      try {
        const response = await getPaymentLink();
        setGatewayLink(response.checkoutLink);
      } catch (error) {
        showErrorMessage(getErrorMessage(error));
      }
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (gatewayLink) {
      window.location.assign(gatewayLink);
    }
  }, [gatewayLink]);

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
            <CheckBoxOutlinedIcon />
            <Typography>
              <Trans>Next Steps</Trans>
            </Typography>
          </Stack>

          <Alert severity="info" variant="outlined">
            <Typography variant="body2">
              <Trans>Here are your next steps to continue with the application process.</Trans>
            </Typography>
          </Alert>

          <Stack
            direction="row"
            sx={{ backgroundColor: theme.palette.menuColor, borderRadius: theme.spacing(1) }}
            justifyContent="space-between"
            gap={1}
            mt={1.5}
            p={2}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <TaskOutlinedIcon sx={{ color: theme.palette.primary.main }} />
              <Stack>
                <Typography variant="h3" fontSize="18px">
                  <Trans>Pay Deposit</Trans>
                </Typography>
                <Typography variant="caption" color={theme.palette.secondary.main}>
                  <Trans>Make your initial deposit to secure your placement</Trans>
                  {paymentProvider && (
                    <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
                      (via {paymentProvider})
                    </Typography>
                  )}
                </Typography>
              </Stack>
            </Stack>
            <Button onClick={handleOpen} variant="contained" size="large">
              <Typography fontSize="inherit" lineHeight="inherit" component="span">
                <Trans>Pay Deposit</Trans>
              </Typography>
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <PaymentModal onContinuePayment={onContinueToGateway} open={open} onClose={handleClose} />
    </>
  );
};
